import React, { useState, useEffect, lazy } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "../lib/apiClient";
import {
  Crown, Trash2, Inbox as InboxIcon, MessageSquare, Clock,
  ShieldAlert, BarChart3, Sparkles, MapPin, HardDrive, ShieldCheck,
  Search as SearchIcon, MessageCircle, Mail, ClipboardList, Activity,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { BACKEND_URL } from "../config/api";
import { useAuth } from "../context/AuthContext";
import ChairmansBazooka from "../components/admin/ChairmansBazooka";
import CollapsibleAdminSection from "../components/admin/CollapsibleAdminSection";
import ActivityPulseStrip from "../components/admin/ActivityPulseStrip";

// Lazy-load every admin panel so the landing paints quickly. Each
// panel JS bundle is fetched only when the section expands.
const MaintenancePanel = lazy(() => import("../components/admin/MaintenancePanel"));
const StoragePanel = lazy(() => import("../components/admin/StoragePanel"));
const AbuseSignalsPanel = lazy(() => import("../components/admin/AbuseSignalsPanel"));
const SearchAnalyticsPanel = lazy(() => import("../components/admin/SearchAnalyticsPanel"));
const CommissionSeedPanel = lazy(() => import("../components/admin/CommissionSeedPanel"));
const CommissionVelocityPanel = lazy(() => import("../components/admin/CommissionVelocityPanel"));
const SmsBudgetPanel = lazy(() => import("../components/admin/SmsBudgetPanel"));
const EmailQuotaPanel = lazy(() => import("../components/admin/EmailQuotaPanel"));
const AdminAuditPanel = lazy(() => import("../components/admin/AdminAuditPanel"));
const XFunnelPanel = lazy(() => import("../components/admin/XFunnelPanel"));

// KPI cell — used across the analytics grid
const StatCard = ({ label, value, sub, accent = "emerald" }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-lg p-3 sm:p-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <p className="text-gray-500 text-[10px] uppercase tracking-wider">{label}</p>
    <p className={`text-${accent}-400 text-2xl sm:text-3xl font-bold mt-1`}>{value}</p>
    {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
  </div>
);

// Tiny section heading between accordion groups so the panel list stays scannable.
const AdminPanelGroupHeader = ({ label }) => (
  <div className="flex items-center gap-3 pt-3 pb-1 first:pt-0">
    <span className="text-gray-400 text-[10px] font-semibold uppercase tracking-[0.15em]">{label}</span>
    <div className="flex-1 h-px bg-gray-800/80" />
  </div>
);

// Tiny dependency-free sparkline for the 14-day series cards
const SparkBars = ({ data, color = "emerald" }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((d) => (
        <div
          key={d.date}
          title={`${d.date}: ${d.count}`}
          className={`flex-1 bg-${color}-500/70 rounded-t hover:bg-${color}-400 transition-colors`}
          style={{ height: `${Math.max(2, (d.count / max) * 100)}%` }}
        />
      ))}
    </div>
  );
};

// Master Admin Console — KPIs, timeseries, controls, retention.
const AdminConsolePage = () => {
  const { user, token } = useAuth();
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [topCreators, setTopCreators] = useState([]);
  const [topVideos, setTopVideos] = useState([]);
  const [regions, setRegions] = useState({});
  const [users, setUsers] = useState([]);
  const [inbox, setInbox] = useState({ items: [], unread: 0 });
  const [supportStats, setSupportStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveChatTtl, setLiveChatTtl] = useState(20);
  const [savingTtl, setSavingTtl] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      // Promise.allSettled (not .all) so a single slow endpoint can't
      // hang the entire dashboard — we render partial data and the
      // operator can refresh if a piece is missing.
      const results = await Promise.allSettled([
        api.get('/admin/analytics/overview'),
        api.get('/admin/analytics/funnel'),
        api.get('/admin/analytics/top-creators?limit=10'),
        api.get('/admin/analytics/top-videos?limit=10'),
        api.get('/admin/analytics/regions'),
        api.get('/admin/users?limit=100'),
        api.get(`/settings/live-chat`),
        api.get('/admin/contact-messages?limit=8'),
        api.get(`/stats/support`),
      ]);
      if (cancelled) return;
      const [ov, fn, tc, tv, rg, us, ttl, ib, ss] = results;
      if (ov.status === "fulfilled") setOverview(ov.value.data);
      if (fn.status === "fulfilled") setFunnel(fn.value.data);
      if (tc.status === "fulfilled") setTopCreators(tc.value.data);
      if (tv.status === "fulfilled") setTopVideos(tv.value.data);
      if (rg.status === "fulfilled") setRegions(rg.value.data);
      if (us.status === "fulfilled") setUsers(us.value.data);
      if (ib.status === "fulfilled") setInbox(ib.value.data);
      if (ss.status === "fulfilled") setSupportStats(ss.value.data);
      if (ttl.status === "fulfilled" && ttl.value.data?.ttl_days) setLiveChatTtl(ttl.value.data.ttl_days);
      // Surface a toast if any failed — but only one (don't spam).
      const firstReject = results.find(r => r.status === "rejected");
      if (firstReject) {
        const err = firstReject.reason;
        toast.error(err?.response?.data?.detail || "Some analytics didn't load — refresh to retry.");
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, token]);

  if (!user) return <Navigate to="/" replace />;
  if (!user.is_admin) {
    return (
      <div className="container mx-auto px-4 py-12 text-center text-gray-400">
        Admin access required.
      </div>
    );
  }

  const saveTtl = async () => {
    const n = parseInt(liveChatTtl, 10);
    if (Number.isNaN(n) || n < 1 || n > 365) {
      toast.error("Enter a value between 1 and 365");
      return;
    }
    setSavingTtl(true);
    try {
      await api.put('/admin/settings/live-chat', { ttl_days: n });
      toast.success(`Live Chat retention set to ${n} day${n === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSavingTtl(false);
  };

  const patchUser = async (username, body) => {
    try {
      const res = await api.patch(`/admin/users/${username}`, body);
      setUsers((prev) => prev.map((u) => u.username === username ? { ...u, ...res.data } : u));
      toast.success(`Updated /${username}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const grantCredits = async (username) => {
    const raw = prompt(`Grant credits to /${username}. Use a negative number to deduct.`, "10");
    if (raw == null) return;
    const amt = parseFloat(raw);
    if (Number.isNaN(amt)) return;
    await patchUser(username, { grant_credits: amt });
  };

  // Renaming is namespaced rewrite (back-fills videos/comments/gifts/etc.
  // for the master-channel content). The endpoint enforces all the same
  // blocklists as registration; we still do a final "are you sure" since
  // public profile URLs change immediately.
  const renameUser = async (username) => {
    const raw = prompt(
      `Rename /${username} to a new handle.\n\n` +
      `Rules: 3–30 chars, lowercase a–z, 0–9, _ or -.\n` +
      `This rewrites every video, comment, gift, and listing under their master channel.`,
      ""
    );
    if (raw == null) return;
    const next = raw.trim().toLowerCase();
    if (!next || next === username) return;
    if (!window.confirm(`Rename /${username} → /${next}? This is reversible only by another rename.`)) return;
    try {
      const res = await api.post(
        `/admin/users/${username}/rename`,
        { new_username: next }
      );
      setUsers((prev) => prev.map((u) => u.username === username ? { ...u, username: res.data.new_username } : u));
      const rowsTotal = Object.values(res.data.rows_updated || {}).reduce((a, b) => a + b, 0);
      toast.success(`Renamed → /${res.data.new_username} (${rowsTotal} rows back-filled)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rename failed");
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm("Delete this video permanently?")) return;
    try {
      await api.delete(`/admin/videos/${videoId}`);
      setTopVideos((prev) => prev.filter((v) => v.id !== videoId));
      toast.success("Video deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const tierBreakdown = overview?.users?.tier_breakdown || {};

  return (
    <div className="container mx-auto px-4 py-6 sm:py-8 max-w-7xl">
      <div className="flex items-center gap-2 mb-1">
        <Crown className="h-6 w-6 text-amber-400" />
        <h1 className="text-2xl sm:text-3xl font-bold text-white">Master Admin Console</h1>
      </div>
      <p className="text-gray-500 text-sm mb-6">
        Site-wide analytics and controls. {overview?.as_of && `As of ${new Date(overview.as_of).toLocaleString()}`}
      </p>

      {/* API documentation links — auto-generated from FastAPI's mounted routers.
          Acts as a live spec for every endpoint across all 10 routers. */}
      <div className="flex flex-wrap items-center gap-2 mb-6 text-xs sm:text-sm" data-testid="admin-api-docs">
        <Link to="/admin/inbox"
              data-testid="admin-inbox-link"
              className="bg-amber-600/15 border border-amber-500/40 hover:bg-amber-600/25 hover:border-amber-400 text-amber-300 hover:text-amber-200 rounded px-3 py-1 transition font-semibold inline-flex items-center gap-1.5">
          <InboxIcon className="h-3.5 w-3.5" /> Inbox
          {inbox.unread > 0 && (
            <span data-testid="admin-inbox-link-badge"
                  className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
              {inbox.unread > 99 ? '99+' : inbox.unread}
            </span>
          )}
        </Link>
        <span className="text-gray-400">Live API spec (10 routers):</span>
        <a href={`${BACKEND_URL}/api/docs`} target="_blank" rel="noopener noreferrer"
           data-testid="admin-api-docs-swagger"
           className="bg-gray-900 border border-gray-700 hover:border-emerald-600 text-emerald-300 hover:text-emerald-200 rounded px-3 py-1 transition">
          Swagger UI
        </a>
        <a href={`${BACKEND_URL}/api/redoc`} target="_blank" rel="noopener noreferrer"
           data-testid="admin-api-docs-redoc"
           className="bg-gray-900 border border-gray-700 hover:border-cyan-600 text-cyan-300 hover:text-cyan-200 rounded px-3 py-1 transition">
          ReDoc
        </a>
        <a href={`${BACKEND_URL}/api/openapi.json`} target="_blank" rel="noopener noreferrer"
           data-testid="admin-api-docs-openapi"
           className="bg-gray-900 border border-gray-700 hover:border-gray-500 text-gray-400 hover:text-gray-200 rounded px-3 py-1 transition">
          openapi.json
        </a>
      </div>

      {/* The Chairman's Bazooka — VIP-invite firing console. Always
          visible to admins regardless of analytics-load state. */}
      <ChairmansBazooka />

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
        </div>
      ) : !overview ? (
        <Card className="bg-gray-900 border-gray-800" data-testid="admin-load-failed">
          <CardContent className="p-6 text-gray-400 text-sm">
            Couldn't load admin analytics. If you just enabled 2FA, try refreshing the page.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Top KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard label="Total Users" value={overview.users.total} sub={`+${overview.users.new_7d} this week`} />
            <StatCard label="Total Videos" value={overview.videos.total} sub={`+${overview.videos.new_7d} this week`} accent="cyan" />
            <StatCard label="Loops" value={overview.videos.total_loops.toLocaleString()} sub="lifetime plays" accent="purple" />
            <StatCard label="Applauds" value={overview.videos.total_applauds.toLocaleString()} sub="lifetime" accent="amber" />
            <StatCard label="Follows" value={overview.engagement.follows} accent="rose" />
            <StatCard label="Credits in Flight" value={overview.economy.total_credits_in_circulation.toLocaleString()} sub="all balances" accent="emerald" />
          </div>

          {/* Support Inbox preview — newest threads + unread count + jump to full triage */}
          <Card className={`${inbox.unread > 0 ? 'bg-amber-950/30 border-amber-500/40' : 'bg-gray-900 border-gray-800'}`} data-testid="admin-support-inbox-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <InboxIcon className={`h-4 w-4 ${inbox.unread > 0 ? 'text-amber-400' : 'text-gray-400'}`} />
                <CardTitle className="text-white text-sm">Support inbox</CardTitle>
                {inbox.unread > 0 && (
                  <span data-testid="admin-support-inbox-unread"
                        className="text-[10px] uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded-full px-2 py-0.5">
                    {inbox.unread} unread
                  </span>
                )}
              </div>
              <Link to="/admin/inbox" className="text-emerald-400 hover:text-emerald-300 text-xs" data-testid="admin-support-inbox-open-all">
                Open inbox →
              </Link>
            </CardHeader>
            <CardContent>
              {inbox.items.length === 0 ? (
                <p className="text-gray-500 text-xs flex items-center gap-2 py-2">
                  <MessageSquare className="h-3.5 w-3.5" /> No support threads yet. When a user opens one from /contact, it'll surface here.
                </p>
              ) : (
                <div className="divide-y divide-gray-800">
                  {inbox.items.slice(0, 5).map((t) => {
                    const unread = (t.unread_for_admin || 0) > 0;
                    return (
                      <Link
                        key={t.id}
                        to={`/admin/inbox/${t.id}`}
                        className="flex items-start justify-between gap-3 py-2.5 hover:bg-gray-800/40 -mx-2 px-2 rounded transition-colors"
                        data-testid={`admin-support-inbox-row-${t.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            {unread && <span className="h-2 w-2 rounded-full bg-amber-400 shrink-0" />}
                            <p className={`truncate text-sm ${unread ? 'text-white font-semibold' : 'text-gray-300'}`}>
                              {t.subject || '(no subject)'}
                            </p>
                          </div>
                          <p className="text-gray-500 text-[11px] mt-0.5">
                            from <span className="text-emerald-400">/{t.username}</span>
                            {t.status === 'closed' && <span className="text-gray-600"> · closed</span>}
                            {t.last_message_at && (
                              <span className="text-gray-600"> · {new Date(t.last_message_at).toLocaleString()}</span>
                            )}
                          </p>
                        </div>
                        {unread && (
                          <span className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-black text-[10px] font-bold flex items-center justify-center">
                            {t.unread_for_admin}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* 14-day timeseries + funnel */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm">Sign-ups · last 14 days</CardTitle>
                <CardDescription className="text-gray-500 text-xs">{overview.users.new_30d} new in last 30 days</CardDescription>
              </CardHeader>
              <CardContent><SparkBars data={overview.signups_14d} color="emerald" /></CardContent>
            </Card>
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm">Videos published · last 14 days</CardTitle>
                <CardDescription className="text-gray-500 text-xs">{overview.videos.new_30d} new in last 30 days</CardDescription>
              </CardHeader>
              <CardContent><SparkBars data={overview.videos_14d} color="cyan" /></CardContent>
            </Card>
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm">Conversion funnel</CardTitle>
                <CardDescription className="text-gray-500 text-xs">From signup → producer → paid</CardDescription>
              </CardHeader>
              <CardContent className="space-y-1.5 text-sm">
                {[
                  ["Signed up", funnel.signed_up, "emerald"],
                  ["Produced 1+ video", funnel.produced_1_video, "cyan"],
                  ["Produced 5+ videos", funnel.produced_5_videos, "purple"],
                  ["On paid tier", funnel.paid_tier, "amber"],
                ].map(([label, n, color]) => {
                  const pct = funnel.signed_up ? Math.round((n / funnel.signed_up) * 100) : 0;
                  return (
                    <div key={label}>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400">{label}</span>
                        <span className="text-gray-300">{n} <span className="text-gray-500">({pct}%)</span></span>
                      </div>
                      <div className="bg-gray-800 rounded-full h-1.5 overflow-hidden">
                        <div className={`bg-${color}-500 h-full rounded-full transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Tiers + Engagement + Regions row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader className="pb-2"><CardTitle className="text-white text-sm">Tier breakdown</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 text-sm">
                {Object.entries(tierBreakdown).map(([tier, count]) => (
                  <div key={tier} className="flex justify-between text-gray-300">
                    <span className="capitalize">{tier}</span>
                    <span className="text-emerald-400 font-mono">{count}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader className="pb-2"><CardTitle className="text-white text-sm">Engagement totals</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 text-sm">
                <div className="flex justify-between text-gray-300"><span>Comments on videos</span><span className="text-cyan-400 font-mono">{overview.engagement.comments}</span></div>
                <div className="flex justify-between text-gray-300"><span>Home live-chat msgs</span><span className="text-cyan-400 font-mono">{overview.engagement.home_chats}</span></div>
                <div className="flex justify-between text-gray-300"><span>Channel-chat msgs</span><span className="text-cyan-400 font-mono">{overview.engagement.channel_chats}</span></div>
                <div className="flex justify-between text-gray-300"><span>Follows</span><span className="text-cyan-400 font-mono">{overview.engagement.follows}</span></div>
              </CardContent>
            </Card>
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader className="pb-2"><CardTitle className="text-white text-sm">Videos by region</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 text-sm">
                {Object.entries(regions).map(([r, n]) => (
                  <div key={r} className="flex justify-between text-gray-300">
                    <span>{r}</span>
                    <span className="text-purple-400 font-mono">{n}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Top creators + top videos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader className="pb-2"><CardTitle className="text-white text-sm">Top creators</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-gray-500 uppercase tracking-wider">
                      <tr><th className="text-left pb-2">User</th><th className="text-right">Videos</th><th className="text-right">Loops</th><th className="text-right">Claps</th><th className="text-right">Fol</th></tr>
                    </thead>
                    <tbody className="text-gray-300">
                      {topCreators.map((c) => (
                        <tr key={c.username} className="border-t border-gray-800">
                          <td className="py-1.5"><Link to={`/${c.username}`} className="text-emerald-400 hover:underline">/{c.username}</Link> <span className="text-gray-600 text-[10px]">{c.tier}</span></td>
                          <td className="text-right">{c.videos}</td>
                          <td className="text-right">{c.loops}</td>
                          <td className="text-right">{c.applauds_received}</td>
                          <td className="text-right">{c.followers}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader className="pb-2"><CardTitle className="text-white text-sm">Top videos</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-gray-500 uppercase tracking-wider">
                      <tr><th className="text-left pb-2">Title</th><th className="text-right">Loops</th><th className="text-right">Claps</th><th /></tr>
                    </thead>
                    <tbody className="text-gray-300">
                      {topVideos.map((v) => (
                        <tr key={v.id} className="border-t border-gray-800">
                          <td className="py-1.5">
                            <span className="text-white">{v.title || '(untitled)'}</span>{' '}
                            <Link to={`/${v.username}`} className="text-emerald-400 hover:underline">/{v.username}</Link>
                            {v.featured && <span className="ml-2 text-amber-400 text-[10px]">★</span>}
                          </td>
                          <td className="text-right">{v.loop_count || 0}</td>
                          <td className="text-right">{v.applaud_count || 0}</td>
                          <td className="text-right">
                            <button onClick={() => deleteVideo(v.id)} title="Delete video" className="text-rose-400 hover:text-rose-300" data-testid={`admin-delete-video-${v.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* User management */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm">Users (latest 100)</CardTitle>
              <CardDescription className="text-gray-500 text-xs">Grant credits, toggle admin or disable accounts.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-gray-500 uppercase tracking-wider">
                    <tr>
                      <th className="text-left pb-2">User</th>
                      <th className="text-left">Tier</th>
                      <th className="text-right">Credits</th>
                      <th className="text-right">Videos</th>
                      <th className="text-right">Fol</th>
                      <th className="text-right">Status</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    {users.map((u) => (
                      <tr key={u.id} className="border-t border-gray-800">
                        <td className="py-1.5">
                          <Link to={`/${u.username}`} className="text-emerald-400 hover:underline">/{u.username}</Link>
                          <div className="text-gray-600 text-[10px]">{u.email}</div>
                        </td>
                        <td className="capitalize text-gray-400">{u.tier}</td>
                        <td className="text-right font-mono">
                          {u.credits.toFixed(2)}
                          {u.monthly_balance > 0 && <span className="text-amber-400 text-[10px] ml-1">({u.monthly_balance.toFixed(0)}m)</span>}
                        </td>
                        <td className="text-right">{u.videos}</td>
                        <td className="text-right">{u.followers}</td>
                        <td className="text-right">
                          {u.is_admin && <span className="text-amber-400 text-[10px] mr-1">ADMIN</span>}
                          {u.is_disabled && <span className="text-rose-400 text-[10px]">DISABLED</span>}
                          {!u.is_admin && !u.is_disabled && <span className="text-gray-600 text-[10px]">active</span>}
                        </td>
                        <td className="text-right space-x-2 whitespace-nowrap">
                          <button onClick={() => grantCredits(u.username)} className="text-emerald-400 hover:text-emerald-300" data-testid={`admin-grant-${u.username}`}>+credits</button>
                          <button onClick={() => renameUser(u.username)} className="text-sky-400 hover:text-sky-300" data-testid={`admin-rename-${u.username}`}>rename</button>
                          <button onClick={() => patchUser(u.username, { is_admin: !u.is_admin })} className="text-amber-400 hover:text-amber-300" data-testid={`admin-toggle-admin-${u.username}`}>{u.is_admin ? 'unadmin' : 'admin'}</button>
                          <button onClick={() => patchUser(u.username, { is_disabled: !u.is_disabled })} className="text-rose-400 hover:text-rose-300" data-testid={`admin-toggle-disable-${u.username}`}>{u.is_disabled ? 'enable' : 'disable'}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Master controls */}
          <Card className="bg-gray-900 border-amber-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Crown className="h-4 w-4 text-amber-400" /> Master controls
              </CardTitle>
              <CardDescription className="text-gray-500 text-xs">Site-wide settings. More toggles will land here.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border border-gray-800 rounded-lg p-3">
                <p className="text-white text-sm font-semibold mb-1">Master Live Chat retention</p>
                <p className="text-gray-500 text-xs mb-2">How long messages stay on the homepage Live Chat.</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input type="number" min={1} max={365} value={liveChatTtl} onChange={(e) => setLiveChatTtl(e.target.value)} className="bg-gray-800 border-gray-700 text-white w-28" data-testid="admin-live-chat-ttl-input" />
                  <span className="text-gray-400 text-sm">days</span>
                  <Button onClick={saveTtl} disabled={savingTtl} className="bg-amber-600 hover:bg-amber-700" data-testid="admin-save-live-chat-ttl">
                    {savingTtl ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
              <div className="border border-dashed border-gray-700 rounded-lg p-3 text-center">
                <p className="text-gray-500 text-xs">More master toggles coming: credit pricing, default follow seeds, featured slot rules, patron-pitch on/off.</p>
              </div>
            </CardContent>
          </Card>

          <ActivityPulseStrip />

          <AdminPanelGroupHeader label="Operations" />
          <CollapsibleAdminSection id="maintenance" title="Maintenance" icon={ShieldAlert} accent="orange" defaultOpen subtitle="Wipe a user · Sweep ghost followers">
            <MaintenancePanel />
          </CollapsibleAdminSection>
          <CollapsibleAdminSection id="storage" title="Storage" icon={HardDrive} accent="amber" subtitle="Per-tier usage + GC">
            <StoragePanel token={token} />
          </CollapsibleAdminSection>

          <AdminPanelGroupHeader label="Growth" />
          <CollapsibleAdminSection id="search-analytics" title="Search Analytics" icon={SearchIcon} accent="emerald" subtitle="Top searches + content gaps">
            <SearchAnalyticsPanel />
          </CollapsibleAdminSection>
          <CollapsibleAdminSection id="commission-seed" title="Prime the Economy" icon={Sparkles} accent="amber" subtitle="Bulk-seed commissions">
            <CommissionSeedPanel token={token} />
          </CollapsibleAdminSection>
          <CollapsibleAdminSection id="commission-velocity" title="Commission Market Velocity" icon={BarChart3} accent="emerald" subtitle="Throughput + ROI">
            <CommissionVelocityPanel />
          </CollapsibleAdminSection>
          <CollapsibleAdminSection id="x-funnel" title="X / Twitter Funnel" icon={Activity} accent="sky" subtitle="Attribution + conversions">
            <XFunnelPanel token={token} />
          </CollapsibleAdminSection>

          <AdminPanelGroupHeader label="Security" />
          <CollapsibleAdminSection id="abuse-signals" title="Abuse Signals" icon={ShieldCheck} accent="red" subtitle="Cluster alerts + IPs">
            <AbuseSignalsPanel />
          </CollapsibleAdminSection>
          <CollapsibleAdminSection id="admin-audit" title="Admin Audit Log" icon={ClipboardList} accent="purple" subtitle="Recent admin actions">
            <AdminAuditPanel />
          </CollapsibleAdminSection>

          <AdminPanelGroupHeader label="Comms" />
          <CollapsibleAdminSection id="sms-budget" title="SMS Budget" icon={MessageCircle} accent="cyan" subtitle="Twilio spend + caps">
            <SmsBudgetPanel token={token} />
          </CollapsibleAdminSection>
          <CollapsibleAdminSection id="email-quota" title="Email Quota" icon={Mail} accent="blue" subtitle="Resend usage">
            <EmailQuotaPanel />
          </CollapsibleAdminSection>
        </div>
      )}
    </div>
  );
};

export default AdminConsolePage;
